import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

export interface ShareTarget {
  readonly title: string
  readonly text: string
  readonly url: string
}

export interface ShareState {
  /** Empty until something needs announcing. Rendered into a live region. */
  readonly message: string
  readonly share: () => void
}

export const COPIED_MESSAGE = 'Link copiado.'

/** The design's value. Long enough to read, short enough not to linger. */
const MESSAGE_TTL_MS = 2400

/**
 * Device sharing, with feature detection where it belongs — in the handler.
 *
 * The design computed its button label from `navigator.share` at RENDER time,
 * which breaks twice in a prerendered app: the server has no `navigator`, so the
 * HTML and the first client render disagree (a hydration mismatch), and
 * `Compartilhar` and `Copiar link` are different widths, so the correction shifts
 * layout. Here the label is a constant and only the behaviour branches.
 *
 * Three paths, in order of preference:
 *   native share sheet -> nothing to announce, the OS took over
 *   clipboard          -> announce "Link copiado."
 *   neither            -> announce the URL itself, so it can be read and selected
 */
export function useShare(target: ShareTarget): ShareState {
  const [message, setMessage] = useState('')
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  // An uncancelled timer calling setState after teardown is a React warning, and
  // the Lighthouse gate is `errors-in-console: maxLength 0`.
  useEffect(
    () => () => {
      clearTimeout(timeoutRef.current)
    },
    [],
  )

  const announce = useCallback((text: string) => {
    setMessage(text)
    clearTimeout(timeoutRef.current)
    timeoutRef.current = setTimeout(() => {
      setMessage('')
    }, MESSAGE_TTL_MS)
  }, [])

  const share = useCallback(() => {
    // Feature detection in the handler, not at render time — and the calls stay
    // bound to `navigator`, which both APIs require.
    const canShareNatively = typeof navigator.share === 'function'

    if (canShareNatively) {
      navigator.share({ ...target }).catch((error: unknown) => {
        // A dismissed share sheet is a user decision, not a failure.
        if (error instanceof Error && error.name === 'AbortError') {
          return
        }
        announce(target.url)
      })
      return
    }

    const clipboard = navigator.clipboard as Clipboard | undefined

    if (clipboard !== undefined) {
      clipboard.writeText(target.url).then(
        () => {
          announce(COPIED_MESSAGE)
        },
        () => {
          announce(target.url)
        },
      )
      return
    }

    announce(target.url)
  }, [announce, target])

  return useMemo(() => ({ message, share }), [message, share])
}
